import { Link } from "@tanstack/react-router";
import { ChevronRightIcon } from "@heroicons/react/24/outline";

import { routes } from "../../../constants";
import {
  matchRouteByPathname,
  capitalizeWords,
  AppRoutes,
} from "../../../utils";

interface Crumb {
  label: string;
  path: string;
}

export function buildBreadcrumbs(pathname: string): Crumb[] {
  const crumbs: Crumb[] = [{ label: "Home", path: AppRoutes.DASHBOARD }];

  if (pathname === AppRoutes.DASHBOARD || pathname === "/") {
    return crumbs;
  }

  const segments = pathname.split("/").filter(Boolean);
  let prefix = "";

  for (const segment of segments) {
    prefix += `/${segment}`;
    const match = matchRouteByPathname(routes, prefix);
    crumbs.push({
      label: match?.title ?? capitalizeWords(segment),
      path: prefix,
    });
  }

  return crumbs;
}

export default function Breadcrumb({ pathname }: { pathname: string }) {
  const crumbs = buildBreadcrumbs(pathname);
  const lastIndex = crumbs.length - 1;

  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex items-center gap-1 text-sm">
        {crumbs.map((crumb, index) => {
          const isLast = index === lastIndex;
          return (
            <li
              key={crumb.path}
              className={`flex items-center gap-1 ${
                // On mobile only the current page shows
                isLast ? "" : "hidden md:flex"
              }`}
            >
              {index > 0 && (
                <ChevronRightIcon
                  className="h-3.5 w-3.5 text-gray-400 hidden md:block"
                  aria-hidden="true"
                />
              )}
              {isLast ? (
                <span aria-current="page" className="font-medium text-gray-900">
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.path}
                  className="text-gray-500 hover:text-primary transition-colors"
                >
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
